import type { Channel, Partner } from "@expert/shared-types";

export interface FeedbackPayload {
    appName: string;
    caller: FeedbackCaller;
    callSid?: string;
    category: string;
    extraData?: Record<string, unknown>;
    feedbackType: FeedbackType;
    sessionId: string;
    ssoAccessToken: string;
    text: string;
    partner: Partner;
    channel?: Channel;
    isTraining?: boolean;
}
export type PartialFeedbackPayload = Omit<FeedbackPayload, "category" | "text">;

export type FeedbackCaller =
    | "StandaloneHeader"
    | "StandaloneTimeline"
    | "PlatformHeader"
    | "PlatformTimeline"
    | "GuideCard";

export type FeedbackType = "NoTextFeedback" | "FreeTextFeedback";
// Free Text Feedback
const ExpertUniversityCategories = [
    "Audio Issues",
    "Inaccurate Evaluation",
    "Freezing/Connection Issues",
    "Something Else Is Broken",
] as const;
export type ExpertUniversityCategory = (typeof ExpertUniversityCategories)[number];
const FreeTextCategories = ["General feedback", "Something’s broken"] as const;
export type FreeTextCategory = (typeof FreeTextCategories)[number];
export const feedbackUIMappingFreeText = {
    default: {
        title: "We’re all ears!",
        subtitle: "Share issues, things you want, and any other feedback",
        placeholder: {
            "General feedback":
                "Example: I want to be able to text customers a picture of where to find a setting on their phone",
            "Something’s broken": "Example: I can’t type in the chat",
        } as Record<FreeTextCategory, string>,
        bottomText: "We’ll save your progress in case you need to come back.",
        categories: FreeTextCategories,
    },
    expertUniversityDefault: {
        title: "We’re all ears!",
        subtitle:
            "Select the category that best fits your feedback and share any details you can. This helps us understand and address your feedback faster!",
        placeholder: {
            "Audio Issues": "Example: The audio is not clear",
            "Inaccurate Evaluation": "Example: The evaluation results are incorrect",
            "Freezing/Connection Issues": "Example: The app freezes frequently",
            "Something Else Is Broken": "Example: I am having issues with the guided flows",
        } as Record<ExpertUniversityCategory, string>,
        bottomText: "We’ll save your progress in case you need to come back.",
        categories: ExpertUniversityCategories,
    },
    success: {
        title: "Thanks for sharing!",
        subtitle: "We read every response and appreciate all of it",
    },
};

// No Text Feedback
type NoTextCategory =
    | "Training Assist – Technical Issues"
    | "Evaluations Page – Feedback Issues"
    | "Training Assist – Tool Issues"
    | "Technical issues in workspace"
    | "Scout/Expert Assist"
    | "Sales or enrollment issues in workspace"
    | "Device activation guide"
    | "Customer reported issues";
const TechnicalIssuesForVoice = ["Freezing", "Transfers", "Callbacks", "Audio", "Dropped calls", "Other"] as const;
const TechnicalIssuesForChat = ["Message not sending", "Delayed messages", "Trouble changing status", "Other"] as const;

const CustomerReportIssuesForChat = ["Duplicated messages", "Cannot read messages", "Other"] as const;

const TechnicalIssuesForTraining = [
    "Loading delays or timeouts",
    "Page not rendering correctly",
    "Freezing",
    "Audio",
    "Other",
] as const;
const ToolIssuesForTraining = ["Expert Assist", "Guided Flows", "Transcript", "Quick Solve", "Other"] as const;
const EvaluationIssuesForTraining = [
    "Inaccurate or missing feedback",
    "Behaviors not displaying correctly",
    "Evaluation failed to load or formatting issues",
    "Other",
] as const;

const ExpertAssistIssues = [
    "Inaccurate responses",
    "Bot not responding",
    "Outdated responses",
    "Doesn’t recognize device",
    "Unhelpful suggestions",
] as const;

const SalesIssues = [
    "Enrollment failures",
    "Incorrect eligibility",
    "Devices aren’t being detected automatically",
    "Other",
] as const;

const DeviceActivationIssues = ["Wrong steps", "Device not supported", "Other"] as const;

const SERVICE_NOW_URL =
    "https://asurion.service-now.com/serviceportal?id=sc_cat_item&sys_id=de3805660f8706007889af58b1050ea9";

interface feedbackUIMapping {
    title: string;
    subtitle?: string;
    categories: readonly NoTextCategory[];
    subcategories: Partial<Record<NoTextCategory, readonly string[]>>;
}

export const feedbackUIMappingNoTextDefault: feedbackUIMapping = {
    title: "What are you having trouble with?",
    subtitle: `For critical issues, submit a
    <a href='${SERVICE_NOW_URL}' target='_blank'>Service Now</a> ticket—it’s the fastest way to get help!`,
    categories: [
        "Technical issues in workspace",
        "Scout/Expert Assist",
        "Sales or enrollment issues in workspace",
        "Device activation guide",
    ],
    subcategories: {
        "Technical issues in workspace": TechnicalIssuesForVoice,
        "Scout/Expert Assist": ExpertAssistIssues,
        "Sales or enrollment issues in workspace": SalesIssues,
        "Device activation guide": DeviceActivationIssues,
    },
};

export const feedbackUIMappingNoTextChat: feedbackUIMapping = {
    title: "What are you having trouble with?",
    categories: ["Technical issues in workspace", "Customer reported issues"],
    subcategories: {
        "Technical issues in workspace": TechnicalIssuesForChat,
        "Customer reported issues": CustomerReportIssuesForChat,
    },
};

export const feedbackUIMappingNoTextTraining: feedbackUIMapping = {
    title: "What are you having trouble with?",
    subtitle: `For critical issues, submit a
    <a href='${SERVICE_NOW_URL}' target='_blank'>Service Now</a> ticket—it’s the fastest way to get help!`,
    categories: [
        "Training Assist – Technical Issues",
        "Training Assist – Tool Issues",
        "Evaluations Page – Feedback Issues",
    ],
    subcategories: {
        "Training Assist – Technical Issues": TechnicalIssuesForTraining,
        "Training Assist – Tool Issues": ToolIssuesForTraining,
        "Evaluations Page – Feedback Issues": EvaluationIssuesForTraining,
    },
};
/** Helper function to get the correct feedback UI mapping based on channel */
export const getFeedbackUIMappingNoText = (channel?: Channel, isTraining?: boolean): feedbackUIMapping => {
    if (channel === "chat") {
        return feedbackUIMappingNoTextChat;
    }

    // If the agent is in training status, use the training assist feedback mapping
    if (isTraining) {
        return feedbackUIMappingNoTextTraining;
    }

    // Default to voice mapping for all other channels or if no channel is specified
    return feedbackUIMappingNoTextDefault;
};

// UI mapping helpers
export const getTitle = (isSuccess: boolean, feedbackType: FeedbackType, channel?: Channel) => {
    return isSuccess
        ? feedbackUIMappingFreeText.success.title
        : feedbackType === "FreeTextFeedback"
          ? feedbackUIMappingFreeText.default.title
          : getFeedbackUIMappingNoText(channel).title;
};
